import React, { useState, useEffect } from "react";
import { IoIosSearch } from "react-icons/io";
import {
  Download,
  Plus,
  Globe,
  Calendar,
  Bell,
  UserPlus,
  Pencil,
  X,
  Mail,
  Shield,
  CreditCard,
  FileText,
  Check,
  MapPin,
  User,
  KeyRound,
  Eye,
  EyeOff,
  ChevronDown,
  GitMerge,
  History,
  Send,
  Loader2,
} from "lucide-react";
import NouveauContactForm from "../Portefeuille/kyc-lab/organisms/NouveauContactForm";
import { useEtatGlobal } from "../EtatGlobal";
import { dialog } from "../../components/ui/Dialog";
import { showSuccessToast, showStatusToast } from "../../components/ui/Toast";
import FormOverlay from "../../components/ui/FormOverlay";
import { httpClient } from "../../HttpClient";
import CompteMyalfaLink from "./CompteMyalfaLink";
import { MarqueSecret, estSecret } from "../Portefeuille/DossierSecret";

const ANNUAIRE_API = "https://backend-myalfa.vercel.app/api/gi/annuaire";

// Champs dont la modification doit resynchroniser Suivi TNS/IR (voir
// handleQuickUpdateContact et les callbacks onContactCreated) — au-delà du
// simple on/off, la périodicité/régime/caisse/mode de paiement affichés
// dans Suivi TNS proviennent aussi directement de la fiche Annuaire.
const TNS_SYNC_FIELDS = [
  "estTNS",
  "tnsPeriodicite",
  "tnsRegime",
  "tnsOrganisme",
  "tnsPaiement",
  "tnsCaisseRetraite",
  "tnsCommentaire",
];
const IR_SYNC_FIELDS = ["autresMissions"];

const TNS_PERIODICITES = ["Mensuelle", "Trimestrielle", "Semestrielle", "Annuelle"];
// Caisse de COTISATION — distincte de la caisse de retraite ci-dessous.
const TNS_ORGANISMES = ["URSSAF", "CGSS Réunion"];
const TNS_CAISSES_RETRAITE = ["CARPIMKO", "RCI", "CIPAV", "CARMF", "CARCDSF", "IRCEC"];
const TNS_PAIEMENTS = ["Prélèvement", "Virement", "Télépaiement"];

// Valeurs par défaut envoyées à l'ACTIVATION du suivi TNS, pour les seuls
// champs encore vides : une réactivation retrouve donc la configuration
// conservée. Le régime (texte libre) et la caisse de retraite n'ont pas de
// valeur par défaut.
function tnsDefaults(contact) {
  return {
    tnsPeriodicite: contact.tnsPeriodicite || TNS_PERIODICITES[1],
    tnsOrganisme: contact.tnsOrganisme || TNS_ORGANISMES[0],
    tnsPaiement: contact.tnsPaiement || TNS_PAIEMENTS[0],
  };
}

// Liste déroulante dont la valeur courante reste affichée même si elle ne
// fait pas (ou plus) partie des choix proposés.
function withCurrent(options, current) {
  return current && !options.includes(current) ? [...options, current] : options;
}

// Champ texte enregistré à la sortie du champ (pas à chaque frappe), et
// seulement si la valeur a changé.
function BlurSaveField({ value, onSave, disabled, multiline, placeholder, className }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      rows={multiline ? 2 : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft.trim() !== (value || "")) onSave(draft.trim());
      }}
      className={className}
    />
  );
}

function confirmDesactivationTNS() {
  return dialog.confirm(
    "Voulez-vous vraiment désactiver le suivi TNS ? Les informations seront conservées et consultables en réactivant le suivi ou dans l'historique.",
    { title: "Désactiver le suivi TNS", confirmLabel: "Désactiver", variant: "warning" }
  );
}

function formatTnsDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "date inconnue";
  return `${d.toLocaleDateString("fr-FR")} à ${d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

// Historique du suivi TNS — affiché uniquement si la fiche a déjà été
// décochée au moins une fois (sinon rien n'est rendu).
function TnsHistorique({ historique }) {
  const events = Array.isArray(historique) ? historique : [];
  if (!events.some((e) => e.type === "desactivation")) return null;
  return (
    <div className="mt-2 mb-3 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
      <span className="text-[11px] font-medium text-slate-500 flex items-center gap-1 mb-1">
        <History className="w-3 h-3" />
        Historique TNS
      </span>
      <ul className="space-y-1">
        {[...events].reverse().map((e, i) => {
          const c = e.config || {};
          const details = [
            c.periodicite,
            c.regime,
            c.caisseCotisation,
            c.caisseRetraite,
            c.paiement,
          ].filter(Boolean);
          return (
            <li key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="font-medium text-slate-700">
                {e.type === "desactivation" ? "Désactivé" : "Activé"}
              </span>{" "}
              le {formatTnsDate(e.date)}
              {e.auteur ? ` par ${e.auteur}` : ""}
              {details.length > 0 && (
                <span className="block text-slate-400">{details.join(" · ")}</span>
              )}
              {c.commentaire && (
                <span className="block text-slate-400 italic">« {c.commentaire} »</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Interrupteur on/off générique — utilisé pour les services TNS/IR de la
// fiche Annuaire (bascule persistée immédiatement, sans passer par "Modifier").
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
        checked ? "bg-[#840040]" : "bg-slate-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-[2px]"
        }`}
      />
    </button>
  );
}

const COMPTE_TYPE_LABELS = {
  IMPOT_GOUV_PARTICULIER: "Impôt.gouv Particulier",
  FEDER: "FEDER",
  BPI: "BPI",
  CESU: "CESU",
  SSI_EN_LIGNE: "SSI en ligne",
  IMPOT_GOUV_PRO_NON_RATTACHE: "Impôt.gouv Pro non rattaché",
  TESE: "TESE",
  ASP_SYLAE: "ASP SYLAE",
  OPCO: "OPCO",
  CHORUS_PRO: "CHORUS PRO",
  CHOMAGE_PARTIEL: "Chômage Partiel",
  NET_ENTREPRISE: "Net Entreprise",
  AUTOENTREPRENEUR: "Auto-entrepreneur",
  PROBTP: "PROBTP",
  CIPAV: "CIPAV",
  CARDSF: "CARDSF",
  CARMF: "CARMF",
  CARPIMKO: "CARPIMKO",
  AMELI_PRO: "Ameli Pro",
  DEFIMODE: "DEFIMODE",
  MACSF: "MACSF",
  RAAP_IRCEC: "RAAP IRCEC",
  INSEE: "INSEE",
  DIVERS: "Divers",
};

const COMPTE_TYPE_COLORS = {
  IMPOT_GOUV_PARTICULIER: "bg-rose-100 text-rose-700 border-rose-200",
  IMPOT_GOUV_PRO_NON_RATTACHE: "bg-rose-100 text-rose-700 border-rose-200",
  INSEE: "bg-rose-100 text-rose-700 border-rose-200",
  SSI_EN_LIGNE: "bg-blue-100 text-blue-700 border-blue-200",
  CIPAV: "bg-blue-100 text-blue-700 border-blue-200",
  CARDSF: "bg-blue-100 text-blue-700 border-blue-200",
  CARMF: "bg-blue-100 text-blue-700 border-blue-200",
  CARPIMKO: "bg-blue-100 text-blue-700 border-blue-200",
  MACSF: "bg-blue-100 text-blue-700 border-blue-200",
  RAAP_IRCEC: "bg-blue-100 text-blue-700 border-blue-200",
  TESE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ASP_SYLAE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CHOMAGE_PARTIEL: "bg-emerald-100 text-emerald-700 border-emerald-200",
  AUTOENTREPRENEUR: "bg-emerald-100 text-emerald-700 border-emerald-200",
  NET_ENTREPRISE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  FEDER: "bg-violet-100 text-violet-700 border-violet-200",
  BPI: "bg-violet-100 text-violet-700 border-violet-200",
  OPCO: "bg-violet-100 text-violet-700 border-violet-200",
  CHORUS_PRO: "bg-violet-100 text-violet-700 border-violet-200",
  CESU: "bg-amber-100 text-amber-700 border-amber-200",
  PROBTP: "bg-amber-100 text-amber-700 border-amber-200",
  AMELI_PRO: "bg-amber-100 text-amber-700 border-amber-200",
  DEFIMODE: "bg-slate-100 text-slate-600 border-slate-200",
  DIVERS: "bg-teal-100 text-teal-700 border-teal-200",
};

// Idem FicheClientOverlay.js/NouveauContactForm.js : le badge "Divers"
// montre le nom personnalisé donné à la création (Compte.diversLabel).
function getCompteTypeLabel(compte) {
  if (compte.type === "DIVERS") {
    return compte.diversLabel
      ? `${COMPTE_TYPE_LABELS.DIVERS} : ${compte.diversLabel}`
      : COMPTE_TYPE_LABELS.DIVERS;
  }
  return COMPTE_TYPE_LABELS[compte.type] || compte.type;
}

function getInitials(nom, prenoms) {
  const p = (prenoms || "").trim().charAt(0).toUpperCase();
  const n = (nom || "").trim().charAt(0).toUpperCase();
  return p + n || "?";
}

const QUESTIONS_LABELS = [
  "Rencontre et attestation d'identité",
  "Personne politiquement exposée (PPE)",
  "Doutes sur l'intégrité et la réputation",
  "Gel des avoirs",
];

// Accès au portail MyALFA Link d'un contact : identifiants (les mêmes que
// l'onglet MyALFA Link de la fiche client) et mail d'invitation (lien
// « Créer mon mot de passe », sans mot de passe dans le mail — 20/10/2026).
function AccesMyalfaLink({ contact, onEdit }) {
  const [voirMdp, setVoirMdp] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [dernierEnvoi, setDernierEnvoi] = useState(null);
  const actif = contact.accesLink ?? (!!contact.mdp && !contact.accesLinkDesactive);
  const desactive = !actif && !!contact.accesLinkDesactive;

  const envoyerInvitation = async () => {
    const ok = await dialog.confirm(
      `Envoyer à ${contact.email} le mail d'invitation MyALFA Link ? Il contient un lien pour créer son mot de passe (valable 7 jours).`,
      { title: "Mail d'invitation", confirmLabel: "Envoyer" }
    );
    if (!ok) return;
    setEnvoi(true);
    try {
      const { data } = await httpClient.post(`${ANNUAIRE_API}/${contact.id}/invitation-link`);
      setDernierEnvoi(data);
      showStatusToast({ status: "success", titre: "Invitation envoyée", message: `Le mail d'invitation a été envoyé à ${data.envoyeA}.` });
    } catch (err) {
      showStatusToast({ status: "error", titre: "Invitation non envoyée", message: err?.response?.data?.error || "Le mail d'invitation n'a pas pu être envoyé." });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className={`rounded-lg p-4 border ${actif ? "bg-white border-[#9B1B5D]/30" : "bg-white border-slate-200"}`}>
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <img src="/logos-pa/myalfa-link-symbole.png" alt="" className="w-5 h-5 rounded" />
        MyALFA Link
        <span
          className={`ml-auto text-[11px] font-semibold rounded-full px-2 py-0.5 border ${
            actif
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : desactive
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-slate-100 text-slate-500 border-slate-200"
          }`}
        >
          {actif ? "Accès actif" : desactive ? "Accès désactivé" : "Pas d'accès"}
        </span>
      </h3>

      {actif ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-500">Identifiant</span>
            <span className="font-medium text-slate-900 truncate">{contact.email || "-"}</span>
          </div>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-500">Mot de passe</span>
            {contact.mdp ? (
              <span className="flex items-center gap-1.5 font-mono text-slate-900">
                {voirMdp ? contact.mdp : "••••••••"}
                <button type="button" onClick={() => setVoirMdp((v) => !v)} className="text-slate-400 hover:text-slate-600" title={voirMdp ? "Masquer" : "Afficher"}>
                  {voirMdp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </span>
            ) : (
              <span className="text-slate-400 italic">Choisi par le contact sur le portail</span>
            )}
          </div>
          <button
            type="button"
            onClick={envoyerInvitation}
            disabled={envoi || !contact.email}
            title={!contact.email ? "Renseignez l'email du contact (Modifier) pour lui envoyer l'invitation." : undefined}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#840040] px-3 py-2 text-xs font-semibold text-white hover:bg-[#6a0033] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {envoi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {envoi ? "Envoi en cours…" : "Mail d'invitation"}
          </button>
          {dernierEnvoi && (
            <p className="text-[11px] text-emerald-700 flex items-center gap-1">
              <Check className="w-3 h-3" /> Invitation envoyée à {dernierEnvoi.envoyeA} le {new Date(dernierEnvoi.envoyeLe).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            {desactive
              ? "L'accès au portail est désactivé (rien n'a été supprimé). Pour le réactiver, cochez « Accès à MyALFA Link » dans la fiche."
              : "Ce contact n'a pas encore d'accès. Cochez « Accès à MyALFA Link » dans la fiche pour lui en ouvrir un."}
          </p>
          <button type="button" onClick={() => onEdit(contact)} className="text-xs font-medium text-[#9B1B5D] hover:underline">
            Modifier la fiche
          </button>
        </div>
      )}
    </div>
  );
}

function ContactDetailOverlay({ contact, onClose, onEdit, onQuickUpdate }) {
  const initials = getInitials(contact.nom, contact.prenoms);
  const isPPE = contact.ppe === true;
  const isIR = contact.identiteVerifiee === true;
  const dossiersCount = contact.clients?.length || 0;
  const comptesCount = contact.comptes?.length || 0;
  const [showPass, setShowPass] = useState({});
  const [savingField, setSavingField] = useState(null);

  const quickUpdate = async (patch, fieldKey) => {
    setSavingField(fieldKey);
    try {
      await onQuickUpdate(patch);
    } finally {
      setSavingField(null);
    }
  };

  const qReponses = [
    {
      label: QUESTIONS_LABELS[0],
      reponse: contact.question1Reponse,
      commentaire: contact.question1Commentaire,
    },
    {
      label: QUESTIONS_LABELS[1],
      reponse: contact.question2Reponse,
      commentaire: contact.question2Commentaire,
    },
    {
      label: QUESTIONS_LABELS[2],
      reponse: contact.question3Reponse,
      commentaire: contact.question3Commentaire,
    },
    {
      label: QUESTIONS_LABELS[3],
      reponse: contact.question4Reponse,
      commentaire: contact.question4Commentaire,
    },
  ];

  return (
    <div className="overlayAC" onClick={onClose}>
      <div
        className="inoverlayLead"
        style={{ width: "90%", maxWidth: "860px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            zIndex: 10000,
          }}
        >
          <span className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center">
            <X className="w-4 h-4" />
          </span>
        </button>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: "0 5%",
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div className="flex justify-between mb-6 items-center">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
                <span className="text-base font-bold text-white">
                  {initials}
                </span>
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900">
                  {contact.nom} {contact.prenoms}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {isPPE && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-md border border-amber-400 bg-amber-50 text-amber-700">
                      PPE
                    </span>
                  )}
                  {isIR && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 bg-blue-50 text-blue-700 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Identité vérifiée
                    </span>
                  )}
                  {contact.autresMissions && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 flex items-center gap-1">
                      IR
                    </span>
                  )}
                  {contact.estTNS && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-md border border-violet-300 bg-violet-50 text-violet-700 flex items-center gap-1">
                      TNS · {contact.tnsPeriodicite || "Trimestrielle"}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">
                    {dossiersCount} dossier{dossiersCount > 1 ? "s" : ""} lié
                    {dossiersCount > 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="mr-10">
              <button
                onClick={() => {
                  onClose();
                  onEdit(contact);
                }}
                className="cursor-pointer flex items-center gap-1.5 h-8 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Modifier
              </button>
            </div>
          </div>

          {/* Body — two columns, taille fixe quel que soit le contenu (voir
              quickUpdate plus bas) : chaque section s'affiche toujours, avec
              "-" pour les champs vides, pour que la fiche garde la même
              taille d'un contact à l'autre. */}
          <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 260px" }}>
            {/* Left — sections détail */}
            <div className="space-y-4 pb-2">
              {/* Identité */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#9B1B5D]" />
                  Identité
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Date de naissance</span>
                    <span className="font-medium text-slate-900">
                      {contact.dateNaissance || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Ville de naissance</span>
                    <span className="font-medium text-slate-900">
                      {contact.villeNaissance || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Pays de naissance</span>
                    <span className="font-medium text-slate-900">
                      {contact.paysNaissance || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Nationalité</span>
                    <span className="font-medium text-slate-900">
                      {contact.nationalite || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                  Adresse de résidence
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Rue</span>
                    <span className="font-medium text-slate-900">
                      {contact.rue || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Autres adresses</span>
                    <span className="font-medium text-slate-900">
                      {contact.autresAdresses || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Code postal</span>
                    <span className="font-medium text-slate-900">
                      {contact.codePostal || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Ville</span>
                    <span className="font-medium text-slate-900">
                      {contact.villeAdresse || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Pays</span>
                    <span className="font-medium text-slate-900">
                      {contact.paysAdresse || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* TNS */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 m-0">
                    <Shield className="w-4 h-4 text-[#9B1B5D]" />
                    TNS
                  </h3>
                  <ToggleSwitch
                    checked={contact.estTNS === true}
                    disabled={savingField === "estTNS"}
                    onChange={async (next) => {
                      if (!next && !(await confirmDesactivationTNS())) return;
                      quickUpdate(
                        { estTNS: next, ...(next && tnsDefaults(contact)) },
                        "estTNS"
                      );
                    }}
                  />
                </div>
                <TnsHistorique historique={contact.tnsHistorique} />
                {contact.estTNS && (
                  <div className="space-y-2.5 pt-1">
                    <div>
                      <span className="text-xs text-slate-500 block mb-1.5">
                        Périodicité
                      </span>
                      <div className="flex items-center gap-1.5">
                        {TNS_PERIODICITES.map((p) => (
                          <button
                            key={p}
                            type="button"
                            disabled={savingField === "tnsPeriodicite"}
                            onClick={() => quickUpdate({ tnsPeriodicite: p }, "tnsPeriodicite")}
                            className={`flex-1 text-center text-[11px] px-2 py-1.5 rounded-lg border transition-colors ${
                              (contact.tnsPeriodicite || "Trimestrielle") === p
                                ? "border-[#9B1B5D] bg-[#9B1B5D] text-white font-medium"
                                : "border-slate-200 text-slate-500 hover:border-[#9B1B5D]/40"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2.5">
                      <div className="col-span-3">
                        <span className="text-xs text-slate-500 block mb-1">Régime</span>
                        <BlurSaveField
                          value={contact.tnsRegime}
                          disabled={savingField === "tnsRegime"}
                          onSave={(v) => quickUpdate({ tnsRegime: v }, "tnsRegime")}
                          placeholder="Ex. : praticien et auxiliaire médical, artisan..."
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:border-[#9B1B5D]/40"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 block mb-1">Caisse de cotisation</span>
                        <select
                          value={contact.tnsOrganisme || ""}
                          disabled={savingField === "tnsOrganisme"}
                          onChange={(e) => quickUpdate({ tnsOrganisme: e.target.value }, "tnsOrganisme")}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:border-[#9B1B5D]/40"
                        >
                          <option value="">—</option>
                          {withCurrent(TNS_ORGANISMES, contact.tnsOrganisme).map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 block mb-1">Caisse ou régime de retraite</span>
                        <select
                          value={contact.tnsCaisseRetraite || ""}
                          disabled={savingField === "tnsCaisseRetraite"}
                          onChange={(e) => quickUpdate({ tnsCaisseRetraite: e.target.value }, "tnsCaisseRetraite")}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:border-[#9B1B5D]/40"
                        >
                          <option value="">—</option>
                          {withCurrent(TNS_CAISSES_RETRAITE, contact.tnsCaisseRetraite).map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 block mb-1">Mode de paiement</span>
                        <select
                          value={contact.tnsPaiement || ""}
                          disabled={savingField === "tnsPaiement"}
                          onChange={(e) => quickUpdate({ tnsPaiement: e.target.value }, "tnsPaiement")}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:border-[#9B1B5D]/40"
                        >
                          <option value="">—</option>
                          {withCurrent(TNS_PAIEMENTS, contact.tnsPaiement).map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <span className="text-xs text-slate-500 block mb-1">Commentaire TNS</span>
                        <BlurSaveField
                          multiline
                          value={contact.tnsCommentaire}
                          disabled={savingField === "tnsCommentaire"}
                          onSave={(v) => quickUpdate({ tnsCommentaire: v }, "tnsCommentaire")}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:border-[#9B1B5D]/40 resize-y"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* IR */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 m-0">
                    <FileText className="w-4 h-4 text-[#9B1B5D]" />
                    IR
                  </h3>
                  <ToggleSwitch
                    checked={contact.autresMissions === true}
                    onChange={(next) =>
                      quickUpdate({ autresMissions: next }, "autresMissions")
                    }
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#9B1B5D]" />
                  Contact
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Email</span>
                    <span className="font-medium text-slate-900">
                      {contact.email || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Téléphone</span>
                    <span className="font-medium text-slate-900">
                      {contact.telephone || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pièce d'identité */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#9B1B5D]" />
                  Pièce d'identité
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Date d'émission</span>
                    <span className="font-medium text-slate-900">
                      {contact.dateEmission || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Date d'expiration</span>
                    <span className="font-medium text-slate-900">
                      {contact.dateExpiration || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Document</span>
                    {contact.pieceIdentite ? (
                      <a
                        href={contact.pieceIdentite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#7A1533] font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Voir la pièce d'identité
                      </a>
                    ) : (
                      <span className="font-medium text-slate-400">-</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  Documents
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Extrait RBE</span>
                    {contact.extraitRBE ? (
                      <a
                        href={contact.extraitRBE}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#7A1533] font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Voir le document
                      </a>
                    ) : (
                      <span className="font-medium text-slate-400">-</span>
                    )}
                  </div>
                  {contact.autresJustificatifs?.length > 0 ? (
                    contact.autresJustificatifs.map((url, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-slate-500">Fichier {i + 1}</span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#7A1533] font-medium hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Voir le document
                        </a>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Autres justificatifs</span>
                      <span className="font-medium text-slate-400">-</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Autre activité */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  Autre activité
                </h3>
                <p className="text-xs text-slate-700">
                  {contact.autreActivite || "-"}
                </p>
              </div>

              {/* Questionnaire */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#9B1B5D]" />
                  Questionnaire associé / BE
                </h3>
                <div className="space-y-2.5">
                  {qReponses.map((q, i) => (
                    <div key={i} className="flex gap-3 text-xs">
                      <span className="text-slate-500 shrink-0 w-44 leading-snug">
                        {q.label}
                      </span>
                      <div>
                        <span
                          className={`font-semibold ${
                            q.reponse === "Oui"
                              ? "text-emerald-600"
                              : q.reponse === "Non"
                              ? "text-rose-600"
                              : "text-slate-400"
                          }`}
                        >
                          {q.reponse || "-"}
                        </span>
                        {q.commentaire && (
                          <p className="text-slate-500 mt-0.5 italic">
                            {q.commentaire}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Commentaire global */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  Commentaire global
                </h3>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {contact.commentaireGlobal || "-"}
                </p>
              </div>
            </div>

            {/* Right — MyALFA Link, dossiers liés + comptes, toujours affichés */}
            <div className="flex flex-col gap-3">
              <AccesMyalfaLink contact={contact} onEdit={onEdit} />
              <CompteMyalfaLink contact={contact} />

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  Dossiers liés
                </h3>
                {dossiersCount > 0 ? (
                  <div className="flex flex-col gap-2">
                    {contact.clients.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2.5 p-2.5 bg-[#9B1B5D]/5 rounded-lg border border-[#9B1B5D]/10"
                      >
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
                          <FileText className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-xs font-semibold text-slate-900 truncate">
                          {c.nom?.toUpperCase() || "—"}
                        </span>
                        {estSecret(c) && <MarqueSecret taille={11} />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">-</p>
                )}
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-[#9B1B5D]" />
                  Comptes liés
                  <span className="ml-auto text-xs font-normal text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                    {comptesCount}
                  </span>
                </h3>
                {comptesCount > 0 ? (
                  <div className="flex flex-col gap-2">
                    {contact.comptes.map((compte) => {
                      const colorCls =
                        COMPTE_TYPE_COLORS[compte.type] ||
                        "bg-slate-100 text-slate-600 border-slate-200";
                      return (
                        <div
                          key={compte.id}
                          className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 space-y-1"
                        >
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border inline-block ${colorCls}`}
                          >
                            {getCompteTypeLabel(compte)}
                          </span>
                          {compte.identifiant && (
                            <p className="text-[11px] text-slate-600 font-mono truncate">
                              {compte.identifiant}
                            </p>
                          )}
                          {compte.motDePasse && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] text-slate-500 font-mono">
                                {showPass[compte.id]
                                  ? compte.motDePasse
                                  : "••••••"}
                              </span>
                              <button
                                onClick={() =>
                                  setShowPass((p) => ({
                                    ...p,
                                    [compte.id]: !p[compte.id],
                                  }))
                                }
                                className="text-slate-300 hover:text-slate-500 transition-colors"
                              >
                                {showPass[compte.id] ? (
                                  <EyeOff className="w-3 h-3" />
                                ) : (
                                  <Eye className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">-</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Contact card ── */
const PROVENANCE_STYLES = {
  Site:            "bg-blue-100 text-blue-700",
  Recommandation:  "bg-emerald-100 text-emerald-700",
  Achalandage:     "bg-violet-100 text-violet-700",
  Autre:           "bg-slate-100 text-slate-500",
};

function ContactCard({ contact, onView, onEdit, selected, onToggleSelect }) {
  const initials = getInitials(contact.nom, contact.prenoms);
  const isPPE = contact.ppe === true;
  const isIR = contact.identiteVerifiee === true;
  const dossiersCount = contact.clients?.length || 0;
  const clientLabel =
    contact.clients
      ?.map((c) => c.nom)
      .filter(Boolean)
      .join(", ") || "";

  return (
    <div
      onClick={() => onView(contact)}
      className={`bg-white rounded-2xl border p-4 flex flex-col gap-3 hover:shadow-md transition-shadow cursor-pointer ${
        selected ? "border-[#840040] ring-1 ring-[#840040]/30" : "border-slate-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(contact.id)}
          className="w-4 h-4 mt-1 accent-[#840040] cursor-pointer shrink-0"
        />
        <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-slate-500">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 leading-tight truncate">
                {contact.nom} {contact.prenoms}
              </p>
              {clientLabel && (
                <p className="text-[11px] text-slate-400 mt-0.5 truncate flex items-center gap-1">
                  {contact.clients?.some(estSecret) && <MarqueSecret taille={10} />}
                  <span className="truncate">{clientLabel}</span>
                </p>
              )}
              {contact.email && (
                <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                  {contact.email}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {isPPE && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">
                  PPE
                </span>
              )}
              {contact.provenance && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    PROVENANCE_STYLES[contact.provenance] || PROVENANCE_STYLES["Autre"]
                  }`}
                >
                  {contact.provenance}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(contact);
                }}
                title="Modifier"
                className="p-1 rounded-lg text-slate-300 hover:text-[#7A1533] hover:bg-[#fff6ee] transition-colors"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-[11px] text-slate-500">
        {(contact.villeNaissance || contact.paysNaissance) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Globe className="w-3 h-3 shrink-0" />
            <span>
              {[contact.villeNaissance, contact.paysNaissance]
                .filter(Boolean)
                .join(", ")}
            </span>
            {contact.dateNaissance && (
              <>
                <Calendar className="w-3 h-3 shrink-0 ml-1" />
                <span>Né le {contact.dateNaissance}</span>
              </>
            )}
          </div>
        )}
        {contact.dateExpiration && (
          <div className="flex items-center gap-1.5">
            <Bell className="w-3 h-3 shrink-0" />
            <span>CNI exp. {contact.dateExpiration}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-[11px] text-[#7A1533] font-medium">
          {dossiersCount} dossier{dossiersCount > 1 ? "s" : ""}
        </span>
        <div className="flex gap-2">
          {isIR && (
            <span className="px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 bg-blue-50 text-blue-700 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Identité vérifiée
            </span>
          )}
          {contact.autresMissions && (
            <span className="px-2 py-1 text-xs font-semibold rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 flex items-center gap-1">
              IR
            </span>
          )}
          {contact.estTNS && (
            <span className="px-2 py-1 text-xs font-semibold rounded-md border border-violet-300 bg-violet-50 text-violet-700 flex items-center gap-1">
              TNS · {contact.tnsPeriodicite || "Trimestrielle"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col gap-3 animate-pulse">
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-xl bg-slate-200 shrink-0" />
        <div className="flex-1 space-y-1.5 pt-1">
          <div className="h-3 bg-slate-200 rounded w-3/4" />
          <div className="h-2.5 bg-slate-100 rounded w-1/2" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="h-2.5 bg-slate-100 rounded w-full" />
        <div className="h-2.5 bg-slate-100 rounded w-2/3" />
      </div>
      <div className="border-t border-slate-100 pt-2">
        <div className="h-2.5 bg-slate-100 rounded w-1/4" />
      </div>
    </div>
  );
}

/* ── Fusion de doublons ── */
const MERGE_TEXT_FIELDS = [
  { key: "nom", label: "Nom" },
  { key: "prenoms", label: "Prénoms" },
  { key: "email", label: "Email" },
  { key: "telephone", label: "Téléphone" },
  { key: "dateNaissance", label: "Date de naissance" },
  { key: "villeNaissance", label: "Ville de naissance" },
  { key: "paysNaissance", label: "Pays de naissance" },
  { key: "nationalite", label: "Nationalité" },
  { key: "rue", label: "Rue" },
  { key: "autresAdresses", label: "Autres adresses" },
  { key: "codePostal", label: "Code postal" },
  { key: "villeAdresse", label: "Ville (adresse)" },
  { key: "paysAdresse", label: "Pays (adresse)" },
  { key: "dateEmission", label: "Date d'émission (pièce ID)" },
  { key: "dateExpiration", label: "Date d'expiration (pièce ID)" },
  { key: "provenance", label: "Provenance" },
  { key: "autreActivite", label: "Autre activité" },
  { key: "commentaireGlobal", label: "Commentaire global" },
];

const MERGE_BOOL_FIELDS = [
  { key: "ppe", label: "PPE" },
  { key: "identiteVerifiee", label: "Identité vérifiée" },
  { key: "autresMissions", label: "IR (autres missions)" },
  { key: "doutesIntegriteReputation", label: "Doutes intégrité / réputation" },
  { key: "gelDesAvoirs", label: "Gel des avoirs" },
];

const MERGE_QUESTION_FIELDS = [1, 2, 3, 4].map((n) => ({
  reponseKey: `question${n}Reponse`,
  commentaireKey: `question${n}Commentaire`,
  label: QUESTIONS_LABELS[n - 1],
}));

function MergeOverlay({ contacts, onClose, onMerge }) {
  const [keepId, setKeepId] = useState(contacts[0].id);
  const [isMerging, setIsMerging] = useState(false);
  const [chosen, setChosen] = useState(() => {
    const init = {};
    [...MERGE_TEXT_FIELDS, ...MERGE_BOOL_FIELDS].forEach(({ key }) => {
      const withValue = contacts.find(
        (c) => c[key] !== null && c[key] !== undefined && c[key] !== ""
      );
      init[key] = (withValue || contacts[0]).id;
    });
    MERGE_QUESTION_FIELDS.forEach(({ reponseKey }) => {
      const withValue = contacts.find((c) => c[reponseKey]);
      init[reponseKey] = (withValue || contacts[0]).id;
    });
    return init;
  });

  const getContact = (id) => contacts.find((c) => c.id === id);
  const setField = (key, id) =>
    setChosen((prev) => ({ ...prev, [key]: id }));

  const handleConfirm = async () => {
    setIsMerging(true);
    try {
      const data = {};
      [...MERGE_TEXT_FIELDS, ...MERGE_BOOL_FIELDS].forEach(({ key }) => {
        data[key] = getContact(chosen[key])?.[key] ?? null;
      });
      MERGE_QUESTION_FIELDS.forEach(({ reponseKey, commentaireKey }) => {
        const source = getContact(chosen[reponseKey]);
        data[reponseKey] = source?.[reponseKey] ?? null;
        data[commentaireKey] = source?.[commentaireKey] ?? null;
      });
      data.pieceIdentite =
        contacts.find((c) => c.pieceIdentite)?.pieceIdentite ?? null;
      data.extraitRBE = contacts.find((c) => c.extraitRBE)?.extraitRBE ?? null;
      data.autresJustificatifs = [
        ...new Set(contacts.flatMap((c) => c.autresJustificatifs || [])),
      ];

      const mergeIds = contacts.filter((c) => c.id !== keepId).map((c) => c.id);
      await onMerge(keepId, mergeIds, data);
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <div className="overlayAC" onClick={onClose}>
      <div
        className="inoverlayLead"
        style={{ width: "92%", maxWidth: "1000px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            zIndex: 10000,
          }}
        >
          <span className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center">
            <X className="w-4 h-4" />
          </span>
        </button>

        <div style={{ padding: "0 5%", paddingBottom: "24px" }}>
          <div className="mb-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-[#9B1B5D]" />
              Fusionner {contacts.length} fiches
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Choisissez la fiche principale (celle qui sera conservée), puis
              pour chaque champ la valeur à garder.
            </p>
          </div>

          {/* Fiche principale */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-2">
              Fiche principale (conservée après fusion)
            </h3>
            <div className="flex flex-wrap gap-2">
              {contacts.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-colors ${
                    keepId === c.id
                      ? "border-[#840040] bg-[#840040]/5 text-[#840040]"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="keepId"
                    checked={keepId === c.id}
                    onChange={() => setKeepId(c.id)}
                    className="accent-[#840040]"
                  />
                  {c.nom} {c.prenoms}
                </label>
              ))}
            </div>
          </div>

          {/* Comparaison champ par champ */}
          <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-[50vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 w-40">
                    Champ
                  </th>
                  {contacts.map((c) => (
                    <th
                      key={c.id}
                      className="text-left px-3 py-2 font-semibold text-slate-600"
                    >
                      {c.nom} {c.prenoms}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {MERGE_TEXT_FIELDS.map(({ key, label }) => (
                  <tr key={key}>
                    <td className="px-3 py-2 text-slate-500 font-medium">
                      {label}
                    </td>
                    {contacts.map((c) => (
                      <td key={c.id} className="px-3 py-2">
                        <label className="flex items-start gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name={`field-${key}`}
                            checked={chosen[key] === c.id}
                            onChange={() => setField(key, c.id)}
                            className="mt-0.5 accent-[#840040]"
                          />
                          <span className="text-slate-800">
                            {c[key] || "—"}
                          </span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
                {MERGE_BOOL_FIELDS.map(({ key, label }) => (
                  <tr key={key}>
                    <td className="px-3 py-2 text-slate-500 font-medium">
                      {label}
                    </td>
                    {contacts.map((c) => (
                      <td key={c.id} className="px-3 py-2">
                        <label className="flex items-start gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name={`field-${key}`}
                            checked={chosen[key] === c.id}
                            onChange={() => setField(key, c.id)}
                            className="mt-0.5 accent-[#840040]"
                          />
                          <span className="text-slate-800">
                            {c[key] ? "Oui" : "Non"}
                          </span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
                {MERGE_QUESTION_FIELDS.map(
                  ({ reponseKey, commentaireKey, label }) => (
                    <tr key={reponseKey}>
                      <td className="px-3 py-2 text-slate-500 font-medium">
                        {label}
                      </td>
                      {contacts.map((c) => (
                        <td key={c.id} className="px-3 py-2">
                          <label className="flex items-start gap-1.5 cursor-pointer">
                            <input
                              type="radio"
                              name={`field-${reponseKey}`}
                              checked={chosen[reponseKey] === c.id}
                              onChange={() => setField(reponseKey, c.id)}
                              className="mt-0.5 accent-[#840040]"
                            />
                            <span className="text-slate-800">
                              {c[reponseKey] || "—"}
                              {c[commentaireKey] && (
                                <span className="block text-[10px] text-slate-400 italic">
                                  {c[commentaireKey]}
                                </span>
                              )}
                            </span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={onClose}
              disabled={isMerging}
              className="h-8 px-4 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={isMerging}
              className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors disabled:opacity-50"
            >
              <GitMerge className="w-3 h-3" />
              {isMerging ? "Fusion..." : "Fusionner"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ── */
function Annuaire() {
  const {
    annuaireData,
    loadingAnnuaire,
    fetchAnnuaire,
    updateAnnuaireContact,
    addAnnuaireContact,
    mergeAnnuaireContacts,
    fetchSuiviIR,
    fetchSuiviTNS,
  } = useEtatGlobal();
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("alpha-asc");
  const [showFormOverlay, setShowFormOverlay] = useState(false);
  const [showEditOverlay, setShowEditOverlay] = useState(false);
  const [showDetailOverlay, setShowDetailOverlay] = useState(false);
  const [detailContact, setDetailContact] = useState(null);
  const [editContact, setEditContact] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMergeOverlay, setShowMergeOverlay] = useState(false);

  const displayable = annuaireData
    .filter((c) => c.nom || c.prenoms)
    .filter((c) => {
      const tokens = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
      if (tokens.length === 0) return true;
      const combined = `${c.nom || ""} ${c.prenoms || ""} ${c.email || ""}`.toLowerCase();
      return tokens.every((t) => combined.includes(t));
    })
    .sort((a, b) => {
      if (sortOption === "date-asc" || sortOption === "date-desc") {
        const cmp = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        return sortOption === "date-asc" ? cmp : -cmp;
      }
      const cmp = `${a.nom || ""} ${a.prenoms || ""}`
        .toLowerCase()
        .localeCompare(`${b.nom || ""} ${b.prenoms || ""}`.toLowerCase());
      return sortOption === "alpha-desc" ? -cmp : cmp;
    });

  const openDetail = (contact) => {
    setDetailContact(contact);
    setShowDetailOverlay(true);
  };

  const openEdit = (contact) => {
    setEditContact(contact);
    setShowEditOverlay(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const selectedContacts = annuaireData.filter((c) =>
    selectedIds.includes(c.id)
  );

  const handleMerge = async (keepId, mergeIds, data) => {
    try {
      await mergeAnnuaireContacts(keepId, mergeIds, data);
      setShowMergeOverlay(false);
      clearSelection();
      dialog.alert("Fiches fusionnées avec succès.");
    } catch (err) {
      console.error("Erreur fusion contacts:", err);
      dialog.alert("Erreur lors de la fusion. Réessayez.");
    }
  };

  // Bascule TNS/IR (et champs TNS associés) directement depuis l'overlay de
  // détails, sans passer par "Modifier" — même endpoint que le formulaire
  // complet (PUT JSON partiel : les champs absents du body sont ignorés par
  // Prisma, donc rien d'autre n'est écrasé).
  // Optimiste : l'interrupteur bascule immédiatement (avant la réponse
  // réseau), avec juste le toast chargement → confirmation par-dessus ; en
  // cas d'échec, on revient à l'état précédent.
  // Resynchronise Suivi IR/TNS en arrière-plan, sans skeleton (voir
  // fetchSuiviIR/fetchSuiviTNS `silencieux` dans EtatGlobal.js) — la
  // réussite se voit déjà à l'instant où le tableau se met à jour ; un
  // toast n'apparaît que si cette resynchronisation elle-même échoue
  // (les deux écrans resteraient alors désynchronisés jusqu'au prochain
  // rechargement complet).
  const resyncSuiviSilencieux = (fetchFn, label) => {
    fetchFn({ silencieux: true }).catch((err) => {
      console.error(`Erreur resynchronisation Suivi ${label}:`, err);
      showStatusToast({
        status: "error",
        titre: `Suivi ${label} non resynchronisé`,
        message: "La modification est enregistrée, mais l'écran Suivi " + label + " n'a pas pu être mis à jour automatiquement.",
      });
    });
  };

  const handleQuickUpdateContact = async (contact, patch) => {
    const previous = { ...contact };
    updateAnnuaireContact(contact.id, patch);
    setDetailContact((prev) =>
      prev && prev.id === contact.id ? { ...prev, ...patch } : prev
    );
    const toast = showStatusToast({
      status: "loading",
      titre: "Enregistrement...",
      message: "Mise à jour en cours.",
    });
    try {
      const { data } = await httpClient.put(`${ANNUAIRE_API}/${contact.id}`, patch);
      updateAnnuaireContact(contact.id, data);
      setDetailContact((prev) =>
        prev && prev.id === contact.id ? { ...prev, ...data } : prev
      );
      toast.update({
        status: "success",
        titre: "Mis à jour",
        message: "Modifications enregistrées.",
      });
      // Suivi TNS/IR lit ces champs directement depuis Annuaire (statut
      // actif ET périodicité/régime/caisse/mode de paiement affichés dans le
      // tableau) — sans ce refetch, une modification ici n'apparaît dans ces
      // écrans qu'après un rechargement complet (fetchAll), pas immédiatement.
      if (TNS_SYNC_FIELDS.some((k) => k in patch)) resyncSuiviSilencieux(fetchSuiviTNS, "TNS");
      if (IR_SYNC_FIELDS.some((k) => k in patch)) resyncSuiviSilencieux(fetchSuiviIR, "IR");
    } catch (err) {
      console.error("Erreur mise à jour rapide Annuaire:", err);
      updateAnnuaireContact(contact.id, previous);
      setDetailContact((prev) =>
        prev && prev.id === contact.id ? { ...prev, ...previous } : prev
      );
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "La mise à jour n'a pas pu être enregistrée.",
      });
    }
  };

  return (
    <div
      className="px-6 min-h-full overflow-y-auto"
      style={{ background: "#f9f7f5" }}
    >
      {/* Title */}
      <div className="mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 m-0">Annuaire</h1>
          {!loadingAnnuaire && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
              {displayable.length}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Répertoire des contacts et bénéficiaires effectifs
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="relative flex-1 max-w-xs">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un contact..."
            className="pl-8 h-8 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="h-8 pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 appearance-none cursor-pointer focus:outline-none focus:border-gray-300"
            >
              <option value="alpha-asc">Nom (A → Z)</option>
              <option value="alpha-desc">Nom (Z → A)</option>
              <option value="date-desc">Date d'ajout (récent → ancien)</option>
              <option value="date-asc">Date d'ajout (ancien → récent)</option>
            </select>
            <ChevronDown
              size={12}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
          <button className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
            <Download className="w-3 h-3" />
            Exporter
          </button>
          <button
            onClick={() => setShowFormOverlay(true)}
            className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
          >
            <Plus className="w-3 h-3" />
            Ajouter un contact
          </button>
        </div>
      </div>

      {/* Grid */}
      {loadingAnnuaire ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : displayable.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayable.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onView={openDetail}
              onEdit={openEdit}
              selected={selectedIds.includes(c.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <UserPlus className="w-10 h-10 text-slate-200" />
          <p className="text-sm text-slate-400">
            {search
              ? "Aucun contact trouvé pour cette recherche."
              : "Aucun contact dans l'annuaire."}
          </p>
        </div>
      )}

      {/* Overlay — ajouter un contact */}
      {showFormOverlay && (
        <FormOverlay onClose={() => setShowFormOverlay(false)}>
          <NouveauContactForm
            onContactCreated={(created) => {
              // NouveauContactForm a déjà fait le POST — `created` est
              // l'objet complet renvoyé par le serveur (id inclus) : on
              // l'ajoute directement à la liste, sans refetch réseau.
              if (created) addAnnuaireContact(created);
              setShowFormOverlay(false);
              showSuccessToast(
                "Contact ajouté",
                `${created?.nom || ""} ${created?.prenoms || ""}`.trim() ||
                  "Le contact a été ajouté à l'annuaire."
              );
              if (created?.estTNS) resyncSuiviSilencieux(fetchSuiviTNS, "TNS");
              if (created?.autresMissions) resyncSuiviSilencieux(fetchSuiviIR, "IR");
            }}
            onBack={() => setShowFormOverlay(false)}
            onCancel={() => setShowFormOverlay(false)}
          />
        </FormOverlay>
      )}

      {/* Overlay — modifier un contact */}
      {showEditOverlay && editContact && (
        <FormOverlay onClose={() => setShowEditOverlay(false)}>
          <NouveauContactForm
            contact={editContact}
            contactId={editContact.id}
            onContactCreated={(updatedContact) => {
              if (updatedContact && editContact?.id) {
                updateAnnuaireContact(editContact.id, updatedContact);
                if (detailContact?.id === editContact.id) {
                  setDetailContact((prev) => ({ ...prev, ...updatedContact }));
                }
              }
              setShowEditOverlay(false);
              showSuccessToast(
                "Contact modifié",
                `${updatedContact?.nom || ""} ${
                  updatedContact?.prenoms || ""
                }`.trim() || "Les modifications ont été enregistrées."
              );
              // Réconciliation en silence (voir fetchAnnuaire dans
              // EtatGlobal.js) — la mise à jour optimiste ci-dessus ne
              // porte pas les champs recalculés côté serveur (pièce
              // d'identité, justificatifs...), donc pas de flash de
              // skeleton mais un refetch reste utile.
              fetchAnnuaire();
              // Suivi TNS/IR affiche aussi des champs "génériques" du
              // contact (nom, prénoms...) en plus de estTNS/tnsXxx et
              // autresMissions — dès que le contact est (ou était) actif
              // dans l'un des deux, toute modif du formulaire doit s'y
              // refléter, pas seulement un changement des champs TNS/IR eux-mêmes.
              if (updatedContact?.estTNS || editContact?.estTNS)
                resyncSuiviSilencieux(fetchSuiviTNS, "TNS");
              if (updatedContact?.autresMissions || editContact?.autresMissions)
                resyncSuiviSilencieux(fetchSuiviIR, "IR");
            }}
            onBack={() => setShowEditOverlay(false)}
            onCancel={() => setShowEditOverlay(false)}
          />
        </FormOverlay>
      )}

      {/* Overlay — détails d'un contact */}
      {showDetailOverlay && detailContact && (
        <ContactDetailOverlay
          contact={detailContact}
          onClose={() => setShowDetailOverlay(false)}
          onQuickUpdate={(patch) => handleQuickUpdateContact(detailContact, patch)}
          onEdit={(contact) => {
            setShowDetailOverlay(false);
            openEdit(contact);
          }}
        />
      )}

      {/* Barre flottante — sélection multiple pour fusion */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-[#840040] text-white/90 px-5 py-4 text-sm font-bold flex items-center justify-center min-w-[56px]">
              <p>{selectedIds.length}</p>
            </div>
            <div className="flex items-center px-4 gap-6">
              <p className="text-sm font-medium text-slate-700">
                Fiche{selectedIds.length > 1 ? "s" : ""} sélectionnée
                {selectedIds.length > 1 ? "s" : ""}
              </p>
              {selectedIds.length >= 2 && (
                <button
                  onClick={() => setShowMergeOverlay(true)}
                  className="flex items-center gap-1.5 text-slate-600 hover:text-[#840040] transition-colors"
                >
                  <GitMerge size={17.5} />
                  <p className="text-xs font-bold">Fusionner</p>
                </button>
              )}
              <button
                onClick={clearSelection}
                className="flex flex-col items-center cursor-pointer text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={17.5} />
                <p className="text-xs font-bold mt-1">Annuler</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay — fusion de doublons */}
      {showMergeOverlay && selectedContacts.length >= 2 && (
        <MergeOverlay
          contacts={selectedContacts}
          onClose={() => setShowMergeOverlay(false)}
          onMerge={handleMerge}
        />
      )}
    </div>
  );
}

export default Annuaire;
